import { Injectable } from '@angular/core';
import { IExportStopRow } from 'src/app/models';

// Pure format <-> GeoJSON conversions for the route-design import/export feature.
// The heavier libraries (togeojson, tokml, jszip) are dynamically imported so they
// only load when the user actually triggers an import/export.
@Injectable({ providedIn: 'root' })
export class RouteFormatConverter {

  async geojsonToKml(geojson: any): Promise<string> {
    const { toKML } = await import('@placemarkio/tokml');
    return toKML(geojson);
  }

  async kmlToGeojson(kmlText: string): Promise<any> {
    const { kml } = await import('@tmcw/togeojson');
    const doc = new DOMParser().parseFromString(kmlText, 'text/xml');
    return kml(doc);
  }

  async geojsonToKmz(geojson: any): Promise<Blob> {
    const kmlText = await this.geojsonToKml(geojson);
    const JSZip = await this.loadJSZip();
    const zip = new JSZip();
    zip.file('doc.kml', kmlText);
    const bytes = await zip.generateAsync({ type: 'uint8array' });
    return new Blob([bytes as BlobPart], { type: 'application/vnd.google-earth.kmz' });
  }

  private async loadJSZip(): Promise<any> {
    const mod: any = await import('jszip');
    return mod.default ?? mod;
  }

  async kmzToGeojson(buffer: ArrayBuffer): Promise<any> {
    const JSZip = await this.loadJSZip();
    const zip = await JSZip.loadAsync(buffer);
    const kmlEntry = Object.keys(zip.files).find((name) => name.toLowerCase().endsWith('.kml'));
    if (!kmlEntry) throw new Error('No .kml entry found inside the KMZ file');
    const kmlText = await zip.files[kmlEntry].async('string');
    return this.kmlToGeojson(kmlText);
  }

  async stopsToXlsx(rows: IExportStopRow[]): Promise<Blob> {
    const JSZip = await this.loadJSZip();
    const zip = new JSZip();
    zip.file('[Content_Types].xml', CONTENT_TYPES_XML);
    zip.file('_rels/.rels', ROOT_RELS_XML);
    zip.file('xl/workbook.xml', WORKBOOK_XML);
    zip.file('xl/_rels/workbook.xml.rels', WORKBOOK_RELS_XML);
    zip.file('xl/worksheets/sheet1.xml', this.buildSheetXml(rows));
    const bytes = await zip.generateAsync({ type: 'uint8array' });
    return new Blob([bytes as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  }

  async xlsxToStops(buffer: ArrayBuffer): Promise<IExportStopRow[]> {
    const JSZip = await this.loadJSZip();
    const zip = await JSZip.loadAsync(buffer);

    const sheetName = Object.keys(zip.files).find((n) => /^xl\/worksheets\/.*\.xml$/i.test(n));
    if (!sheetName) throw new Error('No worksheet found inside the spreadsheet');
    const sheetXml = await zip.files[sheetName].async('string');

    const sharedFile = zip.files['xl/sharedStrings.xml'];
    const sharedStrings = sharedFile ? this.parseSharedStrings(await sharedFile.async('string')) : [];

    return this.parseSheet(sheetXml, sharedStrings);
  }

  private buildSheetXml(rows: IExportStopRow[]): string {
    const header = this.inlineRow(1, ['Name', 'Latitude', 'Longitude']);
    const body = rows.map((row, i) => {
      const r = i + 2;
      return `<row r="${r}">`
        + `<c r="A${r}" t="inlineStr"><is><t>${escapeXml(row.name ?? '')}</t></is></c>`
        + `<c r="B${r}"><v>${row.latitude}</v></c>`
        + `<c r="C${r}"><v>${row.longitude}</v></c>`
        + `</row>`;
    }).join('');
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
      + `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">`
      + `<sheetData>${header}${body}</sheetData></worksheet>`;
  }

  private inlineRow(r: number, values: string[]): string {
    const cells = values.map((v, i) =>
      `<c r="${columnLetter(i)}${r}" t="inlineStr"><is><t>${escapeXml(v)}</t></is></c>`
    ).join('');
    return `<row r="${r}">${cells}</row>`;
  }

  private parseSharedStrings(xml: string): string[] {
    const doc = new DOMParser().parseFromString(xml, 'text/xml');
    return Array.from(doc.getElementsByTagNameNS('*', 'si')).map((si) =>
      Array.from(si.getElementsByTagNameNS('*', 't')).map((t) => t.textContent ?? '').join('')
    );
  }

  // Reads the first three columns of each data row (header row skipped) as
  // name / latitude / longitude. Handles shared strings, inline strings and numbers.
  private parseSheet(xml: string, sharedStrings: string[]): IExportStopRow[] {
    const doc = new DOMParser().parseFromString(xml, 'text/xml');
    const rows = Array.from(doc.getElementsByTagNameNS('*', 'row'));
    const result: IExportStopRow[] = [];

    for (const row of rows) {
      if (Number(row.getAttribute('r')) === 1) continue;
      const cells: Record<number, string> = {};
      for (const c of Array.from(row.getElementsByTagNameNS('*', 'c'))) {
        const col = columnIndex(c.getAttribute('r') ?? '');
        cells[col] = this.readCell(c, sharedStrings);
      }
      const name = cells[0] ?? '';
      const latitude = Number(cells[1]);
      const longitude = Number(cells[2]);
      if (!name && isNaN(latitude) && isNaN(longitude)) continue;
      result.push({ name, latitude, longitude });
    }
    return result;
  }

  private readCell(cell: Element, sharedStrings: string[]): string {
    const type = cell.getAttribute('t');
    if (type === 'inlineStr') {
      const t = cell.getElementsByTagNameNS('*', 't')[0];
      return t?.textContent ?? '';
    }
    const v = cell.getElementsByTagNameNS('*', 'v')[0];
    const raw = v?.textContent ?? '';
    if (type === 's') return sharedStrings[Number(raw)] ?? '';
    return raw;
  }
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function columnLetter(index: number): string {
  let letter = '';
  let n = index;
  do {
    letter = String.fromCharCode(65 + (n % 26)) + letter;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return letter;
}

function columnIndex(cellRef: string): number {
  const letters = cellRef.replace(/[0-9]/g, '');
  let index = 0;
  for (const ch of letters) index = index * 26 + (ch.charCodeAt(0) - 64);
  return index - 1;
}

const CONTENT_TYPES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
  + `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
  + `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>`
  + `<Default Extension="xml" ContentType="application/xml"/>`
  + `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>`
  + `<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
  + `</Types>`;

const ROOT_RELS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
  + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
  + `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>`
  + `</Relationships>`;

const WORKBOOK_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
  + `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">`
  + `<sheets><sheet name="Stops" sheetId="1" r:id="rId1"/></sheets></workbook>`;

const WORKBOOK_RELS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
  + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
  + `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>`
  + `</Relationships>`;
